import { z } from "zod";

/**
 * What a participant can submit, by slide type.
 *
 * Only the seven ANSWERABLE slide types appear here. `qna` collects questions through
 * `qna.ask`, and `content` collects nothing at all — modelling them as answer variants
 * would make "submit an answer to a title slide" representable, and anything
 * representable eventually arrives.
 *
 * A discriminated union rather than one wide optional object, so a word-cloud payload
 * carrying `optionIds` is rejected instead of silently half-understood.
 */

const uuid = z.string().uuid();

/** Trimmed, non-empty, bounded. Free text arrives from the open internet. */
const shortText = (max: number) =>
  z
    .string()
    .trim()
    .min(1, "cannot be blank")
    .max(max, `must be ${max} characters or fewer`);

export const answerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("mcq"), optionIds: z.array(uuid).min(1).max(10) }).strict(),
  z.object({ type: z.literal("true_false"), optionIds: z.array(uuid).length(1) }).strict(),
  z.object({ type: z.literal("poll"), optionIds: z.array(uuid).min(1).max(10) }).strict(),
  z.object({ type: z.literal("type_answer"), text: shortText(200) }).strict(),
  z.object({ type: z.literal("open_text"), text: shortText(500) }).strict(),
  z
    .object({ type: z.literal("word_cloud"), words: z.array(shortText(40)).min(1).max(10) })
    .strict(),
  z.object({ type: z.literal("scale"), value: z.number().int().finite() }).strict(),
]);

export type Answer = z.infer<typeof answerSchema>;
export type AnswerType = Answer["type"];

/** The slide types a participant can answer. Derived, so it cannot drift. */
export const ANSWERABLE_TYPES = answerSchema.options.map(
  (o) => o.shape.type.value,
) as readonly AnswerType[];

/**
 * The three payload columns on `responses`. Exactly one is populated per answer; the
 * rest are null.
 */
export interface ResponseColumns {
  optionIds: string[] | null;
  textValues: string[] | null;
  numberValue: number | null;
}

/**
 * Map an answer onto its storage columns.
 *
 * Lives here, once, rather than in the API handler and again in the batched-insert
 * worker — two copies of this mapping would drift, and the drift would only show up as
 * wrong data long after the round ended.
 */
export function toResponseColumns(answer: Answer): ResponseColumns {
  const empty: ResponseColumns = { optionIds: null, textValues: null, numberValue: null };

  switch (answer.type) {
    case "mcq":
    case "true_false":
    case "poll":
      return { ...empty, optionIds: answer.optionIds };
    case "type_answer":
    case "open_text":
      return { ...empty, textValues: [answer.text] };
    case "word_cloud":
      return { ...empty, textValues: answer.words };
    case "scale":
      return { ...empty, numberValue: answer.value };
    default: {
      // Adding an answerable slide type without handling it here is a COMPILE error,
      // not a row that silently stores nothing.
      const unreachable: never = answer;
      throw new Error(`unhandled answer type: ${JSON.stringify(unreachable)}`);
    }
  }
}
